import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type { CreateRegionRequest, Region, ReorderRegionDto, UpdateRegionRequest } from "./regionsApi";
import { addRegion, deleteRegion, listRegions, reorderRegion, updateRegion } from "./regionsApi";
const EMPTY_REGIONS: Region[] = [];

export function useRegions() {
  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["regions"],
    queryFn: ({ signal }) => listRegions(signal),
  });

  const addMutation = useMutation({
    mutationFn: (region: CreateRegionRequest) => addRegion(region),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["regions"] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, region }: { id: string; region: UpdateRegionRequest }) => updateRegion(id, region),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["regions"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRegion(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["regions"] }),
  });

  const reorderMutation = useMutation({
    mutationFn: (reorders: ReorderRegionDto[]) => reorderRegion(reorders),
    onSuccess: () => {
      enqueueSnackbar(`Kolejność regionów została zaktualizowana`, { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["regions"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować kolejności regionów. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    regions: data ?? EMPTY_REGIONS,
    isLoading,
    addRegion: addMutation.mutateAsync,
    updateRegion: updateMutation.mutateAsync,
    deleteRegion: deleteMutation.mutateAsync,
    reorderRegions: reorderMutation.mutateAsync,
    isReorderPending: reorderMutation.isPending,
  };
}
